import React, { useState, useEffect, useMemo, useCallback } from "react";
import type { CSSProperties } from "react";
import { css, Button, tokens } from "@balabalabom/ui";

export interface ImagePreviewGalleryProps {
  images: string[];
  label: string;
  previewSize?: number;
}

export function ImagePreviewGallery({
  images,
  label,
  previewSize = 5,
}: ImagePreviewGalleryProps) {
  const [activeImage, setActiveImage] = useState<string | null>(null);

  useEffect(() => {
    if (!activeImage) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveImage(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeImage]);

  const dimension = useMemo(() => `${previewSize}rem`, [previewSize]);

  const containerStyle: CSSProperties = useMemo(
    () => ({
      display: "flex",
      gap: tokens.spacing["3"],
      marginBottom: tokens.spacing["4"],
      flexWrap: "wrap",
    }),
    []
  );

  const buttonStyle = useMemo(
    () =>
      css({
        appearance: "none",
        border: `1px solid ${tokens.colors.border.subtle}`,
        borderRadius: tokens.borderRadius["2xl"],
        padding: tokens.spacing["1"],
        backgroundColor: tokens.colors.surfaces.card,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: tokens.boxShadow.subtle,
        width: dimension,
        height: dimension,
        overflow: "hidden",
        position: "relative",
        transition:
          "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
        "&:focus-visible": {
          outline: `2px solid ${tokens.colors.brand.accent}`,
          outlineOffset: tokens.spacing["1"],
        },
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: tokens.boxShadow.md,
          borderColor: tokens.colors.brand.accent,
          backgroundColor: tokens.colors.surfaces.eventHighlight,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          inset: "0 0 auto 0",
          height: "50%",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 75%)",
          opacity: 0,
          transition: "opacity 0.25s ease",
        },
        "&:hover::before": {
          opacity: 1,
        },
        "&::after": {
          content: '"View"',
          position: "absolute",
          bottom: tokens.spacing["1"],
          right: tokens.spacing["1"],
          backgroundColor: tokens.colors.overlay.warm,
          color: tokens.colors.brand.deep,
          fontSize: tokens.typography.fontSize.sm,
          fontWeight: tokens.typography.fontWeight.semibold,
          padding: `${tokens.spacing["0"]} ${tokens.spacing["1"]}`,
          borderRadius: tokens.borderRadius.lg,
          boxShadow: tokens.boxShadow.sm,
          transition: "transform 0.18s ease, opacity 0.18s ease",
          transform: "translateY(4px)",
          opacity: 0.85,
        },
        "&:hover::after": {
          transform: "translateY(-2px)",
          opacity: 1,
        },
      }),
    [dimension]
  );

  const imageStyle: CSSProperties = useMemo(
    () => ({
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: tokens.borderRadius.lg,
      display: "block",
    }),
    []
  );

  const overlayStyle: CSSProperties = useMemo(
    () => ({
      position: "fixed",
      inset: 0,
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: tokens.colors.overlay.backdrop,
      padding: tokens.spacing["4"],
    }),
    []
  );

  const modalStyle: CSSProperties = useMemo(
    () => ({
      width: "100%",
      maxWidth: "min(90vw, 32rem)",
      backgroundColor: tokens.colors.surfaces.card,
      borderRadius: tokens.borderRadius["3xl"],
      boxShadow: tokens.boxShadow.lg,
      padding: tokens.spacing["4"],
      display: "flex",
      flexDirection: "column",
      gap: tokens.spacing["4"],
    }),
    []
  );

  const modalImageStyle: CSSProperties = useMemo(
    () => ({
      width: "100%",
      objectFit: "contain",
      borderRadius: tokens.borderRadius["2xl"],
      maxHeight: "70vh",
    }),
    []
  );

  const handleActivate = useCallback((src: string) => {
    setActiveImage(src);
  }, []);

  const handleClose = useCallback(() => {
    setActiveImage(null);
  }, []);

  const actionsStyle = useMemo(
    () =>
      css({
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacing["2"],
        "@media (min-width: 640px)": {
          flexDirection: "row",
          justifyContent: "flex-end",
        },
      }),
    []
  );

  const linkStyle = useMemo(
    () =>
      css({
        textDecoration: "none",
        display: "inline-flex",
        width: "100%",
        "@media (min-width: 640px)": { width: "auto" },
      }),
    []
  );

  const actionButtonStyle = useMemo(
    () =>
      css({
        width: "100%",
        "@media (min-width: 640px)": { width: "auto" },
      }),
    []
  );

  if (!images.length) {
    return null;
  }

  return (
    <>
      <div style={containerStyle}>
        {images.map((src, index) => (
          <button
            key={`${src}-${index}`}
            type="button"
            onClick={() => handleActivate(src)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleActivate(src);
              }
            }}
            aria-haspopup="dialog"
            aria-label={`${label} ${index + 1}`}
            style={buttonStyle}
          >
            <img src={src} alt={`${label} ${index + 1}`} style={imageStyle} />
          </button>
        ))}
      </div>

      {activeImage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${label} preview`}
          onClick={handleClose}
          style={overlayStyle}
        >
          <div onClick={(event) => event.stopPropagation()} style={modalStyle}>
            <img
              src={activeImage}
              alt={`${label} preview`}
              style={modalImageStyle}
            />
            <div style={actionsStyle}>
              <a href={activeImage} download style={linkStyle}>
                <Button
                  variant="primary"
                  size="sm"
                  style={actionButtonStyle}
                  className="button-full-mobile"
                >
                  Download
                </Button>
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClose}
                className="button-full-mobile"
                style={actionButtonStyle}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
